import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useError } from '../../contexts/ErrorContext';
import InventoryLayout from './InventoryLayout';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import StockInForm from './StockInForm';
import InventoryBackButton from './InventoryBackButton';
import { useProducts } from './hooks/useProducts';
import { useInventory } from './hooks/useInventory';
import { getPlatformClass } from '../../utils/platformUtils';
import { auth } from '../../firebaseConfig';

function StockInPage() {
  const navigate = useNavigate();
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa');
  const { showErrorToast } = useError();

  // Use Firebase hooks
  const {
    products,
    loading: isLoading,
    error,
    clearErrors
  } = useProducts();
  
  const { performStockIn } = useInventory();

  // Handle stock in save
  const handleStockInSave = async (stockInData) => {
    try {
      // Use the useInventory hook to perform stock in operation
      // This will update the product stock AND create a stock movement record
      await performStockIn({
        ...stockInData,
        createdBy: auth.currentUser?.uid || 'unknown'
      });

      // Show success message
      const productName = stockInData.variantName 
        ? `${stockInData.productName} - ${stockInData.variantName}`
        : stockInData.productName;
      showErrorToast(`Successfully added ${stockInData.quantity} ${productName} to inventory!`, {
        variant: 'info',
        autoHide: true,
        duration: 3000
      });
      
      // Navigate back to inventory dashboard
      navigate('/inventory');
    } catch (error) {
      showErrorToast(`Failed to add stock: ${error.message}`, {
        variant: 'error',
        autoHide: true,
        duration: 5000
      });
      throw error;
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/inventory');
  };

  // Render content based on state
  const renderContent = () => {
    if (error) {
      return (
        <div className="inventory-error">
          <PlatformText variant="h3">Error Loading Products</PlatformText>
          <PlatformText variant="body">{error}</PlatformText>
          <PlatformButton onClick={clearErrors}>Retry</PlatformButton>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="inventory-loading">
          <PlatformText variant="h3">Loading products...</PlatformText>
        </div>
      );
    }

    return (
      <>
        {/* Stock In Form */}
        <StockInForm
          products={products}
          onSave={handleStockInSave}
          onCancel={handleCancel}
        />
      </>
    );
  };

  return (
    <InventoryLayout>
      <div className={`inventory-container ${platformClass}`}>
        {/* Mobile App Bar */}
        {isMobilePlatform && (
          <div className="mobile-app-bar inventory-mobile-app-bar">
            <InventoryBackButton onClick={() => navigate('/inventory')}>Stock In</InventoryBackButton>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobilePlatform && (
          <div className="inventory-page-header">
            <div>
              <InventoryBackButton onClick={() => navigate('/inventory')}>Stock In</InventoryBackButton>
              <PlatformText variant="body" className="inventory-page-subtitle">
                Receive and add stock to your inventory
              </PlatformText>
            </div>
          </div>
        )}

        {renderContent()}
      </div>
    </InventoryLayout>
  );
}

export default StockInPage; 